import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';
import { CalendarDays, RotateCcw } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../hooks/useI18n';
import { streamInvoke } from '../lib/safeInvoke';
import { supabase } from '../lib/supabase';
import { getSignEmoji, getSignName } from '../lib/astrology';
import { PhaseLoader } from '../components/shared/PhaseLoader';
import { ReadingFeedback } from '../components/shared/ReadingFeedback';
import { ReadingActions } from '../components/shared/ReadingActions';
import { ToolPageShell } from '../components/tool';
import { DailyAffirmations } from '../components/shared/DailyAffirmations';
import { EmptyState } from '../components/ui/EmptyState';
import { ReadingBasis } from '../components/shared/ReadingBasis';
import { ReferralBanner } from '../components/shared/ReferralBanner';

import type { NatalChartData } from '../services/transitAnalysis';
import type { ForecastPeriod } from '../services/forecastContext';
import { buildDeepForecastContext, serializeContextForPrompt } from '../services/forecastContext';

// ─── Types ───────────────────────────────────────────────────
type Period = 'daily' | 'weekly' | 'monthly' | 'annual';

// Longer periods generate more tokens and need more time
const PERIOD_TIMEOUT_MS: Record<Period, number> = {
  daily:   35_000,
  weekly:  65_000,
  monthly: 85_000,
  annual:  140_000,
};

// Render a single line of Claude markdown as a React element.
// Handles: ### headings, --- dividers, **bold**, *italic* inline.
function ForecastLine({ line, index }: { line: string; index: number }) {
  const trimmed = line.trim();
  if (!trimmed) return null;

  // Horizontal divider (--- or ═══)
  if (/^[-─═]{3,}$/.test(trimmed)) {
    return <hr key={index} style={{ border: 'none', borderTop: '1px solid var(--border-subtle)', margin: '0.625rem 0' }} />;
  }

  // Heading (# / ## / ###), strip hashes, render as gold label
  const headingMatch = trimmed.match(/^#{1,3}\s+(.*)/);
  if (headingMatch) {
    const text = headingMatch[1].replace(/\*\*/g, '');
    return (
      <p key={index} style={{ fontWeight: 700, color: 'var(--petrol)', marginTop: '1.1rem', marginBottom: '0.2rem', fontSize: 'var(--text-label)' }}>
        {text}
      </p>
    );
  }

  // Regular paragraph, convert **bold** and *italic* to HTML
  const html = trimmed
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');

  // Content from our own authenticated edge function, no user input involved
  // eslint-disable-next-line react/no-danger
  return <p key={index} dangerouslySetInnerHTML={{ __html: html }} />;
}

// ─── Markdown table renderer ────────────────────────────────
function MarkdownTable({ rows }: { rows: string[] }) {
  const parseRow = (line: string) =>
    line.split('|').slice(1, -1).map(c => c.trim());
  const isSep = (line: string) =>
    line.split('|').slice(1, -1).every(cell => /^[\s\-:]+$/.test(cell));

  const validRows = rows.filter(r => r.trim().startsWith('|'));
  if (validRows.length < 2) return null;

  const headers  = parseRow(validRows[0]);
  const dataRows = validRows.slice(1).filter(r => !isSep(r)).map(parseRow);

  return (
    <div style={{ overflowX: 'auto', margin: '1rem 0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-caption)', lineHeight: 1.5 }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{ padding: '0.5rem 0.75rem', background: 'rgba(31,78,95,0.12)', color: 'var(--petrol)', fontWeight: 700, textAlign: 'left', borderBottom: '1px solid var(--border-gold)', whiteSpace: 'nowrap' }}>
                {h.replace(/\*\*/g, '')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, ri) => (
            <tr key={ri} style={{ borderBottom: '1px solid var(--border-subtle)', background: ri % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ padding: '0.5rem 0.75rem', color: 'var(--text-secondary)', verticalAlign: 'top' }}>
                  {/* eslint-disable-next-line react/no-danger */}
                  <span dangerouslySetInnerHTML={{ __html: cell.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Split forecast text into text segments and table segments
type Segment = { type: 'text'; lines: string[] } | { type: 'table'; rows: string[] };
function splitSegments(text: string): Segment[] {
  const lines  = text.split('\n');
  const segs: Segment[] = [];
  let buf: string[] = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i].trim().startsWith('|')) {
      if (buf.length) { segs.push({ type: 'text', lines: [...buf] }); buf = []; }
      const rows: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) { rows.push(lines[i]); i++; }
      segs.push({ type: 'table', rows });
    } else {
      buf.push(lines[i]); i++;
    }
  }
  if (buf.length) segs.push({ type: 'text', lines: buf });
  return segs;
}

// Full analysis view, renders text + inline tables
function ForecastFull({ text }: { text: string }) {
  const segs = splitSegments(text);
  return (
    <div className="reading-text">
      {segs.map((seg, si) =>
        seg.type === 'table'
          ? <MarkdownTable key={si} rows={seg.rows} />
          : seg.lines.map((line, li) => <ForecastLine key={`${si}-${li}`} line={line} index={li} />)
      )}
    </div>
  );
}

// Extract leading emoji from a heading string
function extractEmoji(text: string): [string, string] {
  const m = text.match(/^(\p{Emoji_Presentation}[️‍\p{Emoji_Presentation}]*\s*)/u);
  return m ? [m[1].trim(), text.slice(m[0].length).trim()] : ['✦', text];
}

// Bullet-point summary view, one card per section heading + first sentence
function ForecastBullets({ text }: { text: string }) {
  const lines = text.split('\n');
  type Bullet = { emoji: string; heading: string; summary: string };
  const bullets: Bullet[] = [];

  const isNextHeading = (s: string) =>
    s.match(/^#{1,3}/) || s.match(/^\*\*[^*]{3,}\*\*\s*$/) || s.match(/^[-─═]{3,}/) || s.startsWith('|');

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (!trimmed) continue;

    // Accept ### headings AND **bold-only** lines (entire line is bold) as section headers
    let rawHeading: string | null = null;
    const mdH = trimmed.match(/^#{1,3}\s+(.*)/);
    if (mdH) {
      rawHeading = mdH[1].replace(/\*\*/g, '').trim();
    } else {
      const boldOnly = trimmed.match(/^\*\*([^*]{3,})\*\*\s*$/);
      if (boldOnly) rawHeading = boldOnly[1].trim();
    }
    if (!rawHeading || rawHeading.length < 3) continue;

    const [emoji, heading] = extractEmoji(rawHeading);
    if (!heading) continue;

    let summary = '';
    for (let j = i + 1; j < lines.length; j++) {
      const next = lines[j].trim();
      if (!next) continue;
      if (isNextHeading(next)) break;
      const plain = next.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
      if (plain.length < 15) continue;
      const firstSentence = plain.match(/^[^.!?]+[.!?]/)?.[0] ?? plain;
      summary = firstSentence.length > 220 ? firstSentence.slice(0, 217) + '…' : firstSentence;
      break;
    }

    bullets.push({ emoji, heading, summary });
  }

  // Fallback: extract first sentence from each substantive paragraph
  if (!bullets.length) {
    const paras = text
      .split(/\n{2,}/)
      .map(p => p.trim().replace(/^#{1,3}\s+/, '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1'))
      .filter(p => p.length > 50 && !p.startsWith('|') && !p.match(/^[-─═]{3,}/))
      .slice(0, 7);

    if (!paras.length) return <ForecastFull text={text} />;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {paras.map((p, i) => {
          const sentence = p.match(/^[^.!?]+[.!?]/)?.[0] ?? (p.length > 190 ? p.slice(0, 187) + '…' : p);
          return (
            <div key={i} style={{ display: 'flex', gap: '0.75rem', padding: '0.75rem 1rem', borderRadius: '8px', background: 'rgba(255,255,255,0.025)', border: '1px solid var(--border-subtle)', alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--petrol)', flexShrink: 0, fontSize: '1rem', marginTop: '2px' }}>✦</span>
              <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: 'var(--text-label)', lineHeight: 1.6 }}>{sentence}</p>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
      {bullets.map((b, i) => (
        <div key={i} style={{ display: 'flex', gap: '0.75rem', padding: '0.75rem 1rem', borderRadius: '8px', background: 'rgba(255,255,255,0.025)', border: '1px solid var(--border-subtle)', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '1.2rem', lineHeight: 1, flexShrink: 0, marginTop: '2px' }}>{b.emoji}</span>
          <div>
            <p style={{ fontWeight: 600, color: 'var(--petrol)', margin: '0 0 0.2rem', fontSize: 'var(--text-label)' }}>{b.heading}</p>
            {b.summary && <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: 'var(--text-label)', lineHeight: 1.6 }}>{b.summary}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

interface ForecastCache { [key: string]: string }

// ─── Main page ───────────────────────────────────────────────
export function ForecastPage() {
  useSystemUsageTracker('previsao');
  const { profile } = useAuth();
  const { t, lang } = useI18n();
  const isAdmin = profile?.role === 'admin';
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('daily');
  const [loading,       setLoading]       = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [cache,         setCache]         = useState<ForecastCache>({});
  const [error,         setError]         = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'full' | 'bullets'>('bullets');
  const [natalChart, setNatalChart] = useState<NatalChartData | null>(null);
  const [natalChartLoaded, setNatalChartLoaded] = useState(false);

  const LOADING_PHASES: Record<Period, string[]> = {
    daily: [
      'Consultando os trânsitos de hoje...',
      'Analisando seu mapa natal...',
      'Identificando os temas do dia...',
      'Preparando sua leitura personalizada...',
    ],
    weekly: [
      'Mapeando os movimentos da semana...',
      'Cruzando os trânsitos com seu mapa natal...',
      'Analisando amor, trabalho e energia...',
      'Preparando sua previsão semanal...',
    ],
    monthly: [
      'Consultando os planetas de ' + new Date().toLocaleDateString('pt-BR', { month: 'long' }) + '...',
      'Analisando seu mapa natal em profundidade...',
      'Calculando amor, trabalho, saúde e finanças...',
      'Redigindo sua previsão mensal completa...',
      'Finalizando os detalhes do mês...',
    ],
    annual: [
      'Consultando os grandes ciclos de ' + new Date().getFullYear() + '...',
      'Analisando Saturno, Júpiter e os planetas lentos...',
      'Mapeando os 12 meses do ano para você...',
      'Calculando amor, trabalho e saúde por mês...',
      'Redigindo sua previsão anual completa...',
      'Esta análise é extensa, obrigada pela paciência ✦',
    ],
  };

  const PERIODS: { key: Period; label: string; emoji: string; loading: string }[] = [
    { key: 'daily', label: t('forecast.periodToday'), emoji: '☀️', loading: t('forecast.loadingDaily') },
    { key: 'weekly', label: t('forecast.periodWeek'), emoji: '🌙', loading: t('forecast.loadingWeekly') },
    { key: 'monthly', label: t('forecast.periodMonth'), emoji: '📅', loading: t('forecast.loadingMonthly') },
    { key: 'annual', label: t('forecast.periodYear'), emoji: '🌟', loading: t('forecast.loadingAnnual') },
  ];

  const cacheKey = `${period}-${lang}`;
  const forecast = cache[cacheKey] ?? '';
  const currentPeriod = PERIODS.find(p => p.key === period)!;

  // Fetch natal chart from DB on mount
  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      try {
        const { data } = await supabase
          .from('natal_charts')
          .select('planets, houses, aspects, mc_sign, mc_degree, dominant_element, dominant_modality, dominant_sign, birth_date, birth_time, birth_city')
          .eq('user_id', profile.id)
          .is('invalidated_at', null)
          .order('calculated_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (data) {
          setNatalChart(data as unknown as NatalChartData);
        }
      } catch { /* natal chart not available yet */ } finally {
        // Signal that the lookup is done (success or not) so fetchForecast can proceed
        setNatalChartLoaded(true);
      }
    })();
  }, [profile?.id]);

  // Auto-fetch once natal chart lookup completes (ensures deepContext is always built).
  // Do NOT trigger on profile?.birth_date, that fires before natalChart is loaded.
  useEffect(() => {
    if (natalChartLoaded && profile?.birth_date) fetchForecast(period, isAdmin);
  }, [natalChartLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch when period or language changes (natal chart already loaded at this point)
  useEffect(() => {
    if (natalChartLoaded && profile?.birth_date && (!cache[`${period}-${lang}`] || isAdmin)) fetchForecast(period, isAdmin);
  }, [period, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  function regenerate() {
    setCache(prev => { const n = { ...prev }; delete n[`${period}-${lang}`]; return n; });
    fetchForecast(period, true);
  }

  function fetchForecast(p: Period, force = false) {
    if (cache[`${p}-${lang}`] && !force && !isAdmin) return;
    setLoading(true);
    setStreamingText('');
    setError(null);

    // Build deep context if natal chart is available
    let deepContext: string | undefined;
    if (natalChart && profile?.full_name) {
      try {
        const ctx = buildDeepForecastContext(natalChart, profile.full_name.split(' ')[0], p as ForecastPeriod);
        deepContext = serializeContextForPrompt(ctx);
      } catch (err) {
        console.warn('[forecast] context build error:', err);
      }
    }

    // Use streaming endpoint, text appears word-by-word as Claude generates it
    streamInvoke('forecast-stream', {
      body:    { period: p, deepContext, ...(force ? { force: true } : {}) },
      timeout: PERIOD_TIMEOUT_MS[p],
      onChunk(accumulated) {
        setStreamingText(accumulated);
      },
      onComplete(fullText) {
        if (fullText) {
          setCache(prev => ({ ...prev, [`${p}-${lang}`]: fullText }));
        } else {
          setError(t('forecast.starsMoving'));
        }
        setStreamingText('');
        setLoading(false);
      },
      onError() {
        setError(t('forecast.starsMoving'));
        setStreamingText('');
        setLoading(false);
      },
    });
  }

  // ─── Empty state ────────────────────────────────────────────
  if (!profile?.birth_date) {
    return (
      <ToolPageShell icon={CalendarDays} title={t('forecast.title')}>
        <EmptyState
          emoji="🌙"
          title={t('forecast.emptyTitle')}
          subtitle={t('forecast.emptySubtitle')}
          cta={t('forecast.emptyCta')}
          ctaAction={() => navigate('/profile')}
        />
      </ToolPageShell>
    );
  }

  const signEmoji = getSignEmoji(profile.sun_sign ?? '');
  const signName  = getSignName(profile.sun_sign ?? '');
  const firstName = profile.full_name?.split(' ')[0] ?? '';

  return (
    <ToolPageShell
      icon={CalendarDays}
      title={firstName ? `Previsão Personalizada para ${firstName}` : t('forecast.title')}
      headerRight={forecast && !loading ? (
        <button
          onClick={regenerate}
          className="btn btn-ghost btn-sm"
          style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}
          title={t('forecast.regenerateTitle')}
        >
          <RotateCcw size={13} aria-hidden /> {t('forecast.regenerateCta')}
        </button>
      ) : undefined}
    >
      {/* Period pills */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem', alignItems: 'center' }}>
        {PERIODS.map(p => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={period === p.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}
          >
            <span>{p.emoji}</span> {p.label}
          </button>
        ))}
      </div>

      {/* Forecast card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '1rem' }}>
          {period === 'daily'
            ? `✦ Previsão do Dia ✦ ${new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase())}`
            : `✦ ${currentPeriod.label}`}
        </p>

        {/* Streaming: render plain text during generation to avoid per-chunk re-renders */}
        {loading && streamingText ? (
          <div style={{ position: 'relative' }}>
            <div
              className="reading-text"
              style={{ opacity: 0.92, whiteSpace: 'pre-wrap', fontSize: 'var(--text-label)', lineHeight: 1.75 }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: streamingText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
            />
            <p style={{
              color: 'var(--petrol)',
              fontSize: 'var(--text-caption)',
              marginTop: '1rem',
              fontStyle: 'italic',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
            }}>
              <span style={{ animation: 'pulse 1.4s ease-in-out infinite' }}>✦</span>
              {t('forecast.stillThinking')}
            </p>
          </div>
        ) : loading ? (
          <PhaseLoader phases={LOADING_PHASES[period]} interval={period === 'annual' ? 6000 : 4000} />
        ) : error && !forecast ? (
          <p style={{ color: 'var(--text-tertiary)', fontStyle: 'italic', fontSize: 'var(--text-label)' }}>{error}</p>
        ) : forecast ? (
          <>
            {/* View-mode tabs */}
            <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '1rem', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '0.75rem' }}>
              {(['full', 'bullets'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  style={{
                    padding: '0.35rem 0.875rem',
                    borderRadius: '6px',
                    border: 'none',
                    background: viewMode === mode ? 'rgba(31,78,95,0.15)' : 'transparent',
                    color: viewMode === mode ? 'var(--petrol)' : 'var(--text-muted)',
                    fontSize: 'var(--text-caption)',
                    fontWeight: viewMode === mode ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 150ms ease',
                  }}
                >
                  {mode === 'full' ? '📖 Análise Completa' : '✦ Resumo'}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 0.875rem', borderRadius: '8px', background: 'rgba(31,78,95,0.08)', border: '1px solid rgba(31,78,95,0.2)', marginBottom: '0.875rem', fontSize: 'var(--text-caption)', color: 'var(--petrol)' }}>
              <span>✦</span>
              {viewMode === 'full'
                ? <span>Esta é a <strong>análise completa</strong>. Clique em <strong>✦ Resumo</strong> para a leitura sumarizada.</span>
                : <span>Este é o <strong>resumo</strong>. Clique em <strong>📖 Análise Completa</strong> para a leitura detalhada.</span>
              }
            </div>
            {viewMode === 'full'
              ? <ForecastFull text={forecast} />
              : <ForecastBullets text={forecast} />
            }

            <ReadingActions pageName={`${t('forecast.title')}, ${currentPeriod.label}`} reading={forecast} signEmoji={signEmoji} userName={profile.full_name?.split(' ')[0] ?? undefined} />
            <DailyAffirmations userId={profile.id} />
            <ReadingFeedback feature="forecast" />
            <ReadingBasis
              userName={firstName || undefined}
              sunSign={profile.sun_sign ?? undefined}
              moonSign={profile.moon_sign ?? undefined}
            />
          </>
        ) : null}
      </div>

      {/* ── Free-tier upsell banner ─────────────────────────────────────────── */}
      {forecast && !loading && profile.subscription_tier === 'free' && (
        <div style={{
          padding: '1rem 1.125rem',
          borderRadius: 'var(--radius-card)',
          border: '1px solid rgba(31,78,95,0.3)',
          background: 'linear-gradient(135deg, rgba(31,78,95,0.07) 0%, transparent 100%)',
          marginBottom: '1.25rem',
        }}>
          <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', margin: '0 0 0.625rem', lineHeight: 1.55 }}>
            ✦ Esta é a previsão geral para <strong style={{ color: 'var(--petrol)' }}>{signName}</strong>.
            Sua previsão personalizada, baseada no seu mapa natal completo, vai muito além.
            ela leva em conta cada planeta, casa e aspecto do seu céu de nascimento.
          </p>
          <Link to="/pricing" className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }}>
            Ver planos e desbloquear ✦
          </Link>
        </div>
      )}

      {/* ── Next period cross-sell ──────────────────────────────────────────── */}
      {forecast && !loading && (() => {
        const now = new Date();
        const monthName = now.toLocaleDateString('pt-BR', { month: 'long' });
        const year = now.getFullYear();
        if (period === 'daily') return (
          <div style={{ marginBottom: '1.25rem', padding: '0.875rem 1rem', borderRadius: 'var(--radius-card)', border: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
            <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', margin: '0 0 0.5rem' }}>
              🌙 Quer ver como a <strong>semana toda</strong> se desenrola? Sua previsão semanal completa já está disponível.
            </p>
            <button className="btn btn-secondary btn-sm" onClick={() => setPeriod('weekly')}>
              Ver previsão da semana →
            </button>
          </div>
        );
        if (period === 'weekly') return (
          <div style={{ marginBottom: '1.25rem', padding: '0.875rem 1rem', borderRadius: 'var(--radius-card)', border: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
            <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', margin: '0 0 0.5rem' }}>
              📅 Quer o <strong>mapa completo de {monthName}</strong>? Amor, trabalho, saúde e finanças, tudo no seu mês.
            </p>
            <button className="btn btn-secondary btn-sm" onClick={() => setPeriod('monthly')}>
              Ver previsão de {monthName} →
            </button>
          </div>
        );
        if (period === 'monthly') return (
          <div style={{ marginBottom: '1.25rem', padding: '0.875rem 1rem', borderRadius: 'var(--radius-card)', border: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
            <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', margin: '0 0 0.5rem' }}>
              🌟 Quer ver o que <strong>{year} inteiro</strong> reserva para você? A previsão anual cobre cada mês com amor, trabalho e saúde.
            </p>
            <button className="btn btn-secondary btn-sm" onClick={() => setPeriod('annual')}>
              Ver previsão de {year} →
            </button>
          </div>
        );
        return null;
      })()}

      {/* ── Referral banner ────────────────────────────────────────────────── */}
      <ReferralBanner page="forecast" />

      {/* ── Perguntar aos Astros cross-sell ────────────────────────────────── */}
      {forecast && !loading && (
        <div style={{
          padding: '0.875rem 1rem',
          borderRadius: 'var(--radius-card)',
          border: '1px solid rgba(192,132,252,0.25)',
          background: 'rgba(192,132,252,0.05)',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          flexWrap: 'wrap',
        }}>
          <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
            🔮{' '}
            {period === 'daily' && 'Algo no seu dia de hoje te gerou uma dúvida específica?'}
            {period === 'weekly' && 'A semana trouxe um tema que você quer aprofundar?'}
            {period === 'monthly' && 'Queira entender melhor alguma tendência do seu mês?'}
            {period === 'annual' && 'O panorama do ano levantou uma questão específica para você?'}
            {' '}<strong style={{ color: 'var(--text-primary)' }}>Consulte os astros diretamente.</strong>
          </p>
          <Link to="/questions" className="btn btn-sm" style={{ textDecoration: 'none', background: 'rgba(192,132,252,0.15)', color: '#c084fc', border: '1px solid rgba(192,132,252,0.3)', flexShrink: 0 }}>
            Perguntar aos Astros →
          </Link>
        </div>
      )}
    </ToolPageShell>
  );
}
